export const DEFAULT_LANGUAGE = "en-US" as const;
export const LANGUAGE_STORAGE_KEY = "elysium.language";

export const SUPPORTED_LANGUAGES = [
  { tag: "en-GB", label: "English (United Kingdom)" },
  { tag: "en-US", label: "English (United States)" },
  { tag: "cs-CZ", label: "Čeština" },
  { tag: "da-DK", label: "Dansk" },
  { tag: "de-DE", label: "Deutsch" },
  { tag: "es-ES", label: "Español" },
  { tag: "fr", label: "Français" },
  { tag: "fr-CA", label: "Français (Canada)" },
  { tag: "it-IT", label: "Italiano" },
  { tag: "nl-NL", label: "Nederlands" },
  { tag: "nb-NO", label: "Norsk bokmål" },
  { tag: "pl-PL", label: "Polski" },
  { tag: "pt-BR", label: "Português (Brasil)" },
  { tag: "pt-PT", label: "Português (Portugal)" },
  { tag: "sv-SE", label: "Svenska" },
  { tag: "zh-Hans", label: "简体中文" },
  { tag: "zh-Hant", label: "繁體中文" },
  { tag: "ja-JP", label: "日本語" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["tag"];

export const SUPPORTED_LANGUAGE_TAGS = SUPPORTED_LANGUAGES.map(
  ({ tag }) => tag,
) as SupportedLanguage[];

const LANGUAGE_ALIASES: Record<string, SupportedLanguage> = {
  "en-au": "en-GB",
  "en-nz": "en-GB",
  "zh-cn": "zh-Hans",
  "zh-sg": "zh-Hans",
  "zh-tw": "zh-Hant",
  "zh-hk": "zh-Hant",
  "zh-mo": "zh-Hant",
};

const LANGUAGE_BASE_DEFAULTS: Record<string, SupportedLanguage> = {
  cs: "cs-CZ",
  da: "da-DK",
  de: "de-DE",
  en: DEFAULT_LANGUAGE,
  es: "es-ES",
  fr: "fr",
  it: "it-IT",
  ja: "ja-JP",
  nb: "nb-NO",
  nl: "nl-NL",
  no: "nb-NO",
  pl: "pl-PL",
  pt: "pt-PT",
  sv: "sv-SE",
};

export function matchSupportedLanguage(
  language: string,
): SupportedLanguage | undefined {
  const normalizedLanguage = language.toLowerCase();
  const exactMatch = SUPPORTED_LANGUAGES.find(
    ({ tag }) => tag.toLowerCase() === normalizedLanguage,
  );

  if (exactMatch) return exactMatch.tag;
  if (LANGUAGE_ALIASES[normalizedLanguage])
    return LANGUAGE_ALIASES[normalizedLanguage];
  if (normalizedLanguage.startsWith("zh-hant")) return "zh-Hant";
  if (normalizedLanguage.startsWith("zh-hans")) return "zh-Hans";

  const baseLanguage = normalizedLanguage.split("-")[0];
  return LANGUAGE_BASE_DEFAULTS[baseLanguage];
}

export function getInitialLanguage(): SupportedLanguage {
  const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (storedLanguage) {
    const exactStoredLanguage = SUPPORTED_LANGUAGES.find(
      ({ tag }) => tag === storedLanguage,
    );
    if (exactStoredLanguage) return exactStoredLanguage.tag;
  }

  const browserLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const browserLanguage of browserLanguages) {
    const supportedLanguage = matchSupportedLanguage(browserLanguage);
    if (supportedLanguage) return supportedLanguage;
  }

  return DEFAULT_LANGUAGE;
}

export function storeLanguage(language: SupportedLanguage) {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
}
